/**
 * Agent Prompt Panel
 *
 * Displays and allows editing of the project's agent prompt
 * (the initial prompt that describes what the project should do)
 * Includes interactive chat interface for refining requirements with LLM
 */

import { useState, useEffect } from 'react';
import { useProjectStore } from '@/stores/projectStore';
import { Button } from '../ui/Button';
import { ChatInterface } from '../chat/ChatInterface';
import { MessageSquare, FileText } from 'lucide-react';

export const AgentPromptPanel: React.FC = () => {
  const activeProjectId = useProjectStore((state) => state.activeProjectId);
  const projects = useProjectStore((state) => state.projects);
  const updateProject = useProjectStore((state) => state.updateProject);

  const activeProject = activeProjectId ? projects[activeProjectId] : null;
  const [agentPrompt, setAgentPrompt] = useState(activeProject?.agentPrompt || '');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [viewMode, setViewMode] = useState<'chat' | 'text'>('chat');

  // Update local state when project changes
  useEffect(() => {
    setAgentPrompt(activeProject?.agentPrompt || '');
    setIsEditing(false);
  }, [activeProject?.agentPrompt, activeProjectId]);

  const handleSave = async () => {
    if (!activeProjectId) return;

    setIsSaving(true);
    try {
      await updateProject(activeProjectId, {
        agentPrompt: agentPrompt.trim() || undefined
      });
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save agent prompt:', error);
      alert('Failed to save agent prompt. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setAgentPrompt(activeProject?.agentPrompt || '');
    setIsEditing(false);
  };

  if (!activeProject) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-background">
        <div className="text-center text-muted-foreground">
          <p className="text-sm">No project selected</p>
          <p className="text-xs mt-2">Create or select a project to view its agent prompt</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col bg-background">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div>
          <h3 className="text-sm font-medium">Skills Agent</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Chat with AI to refine requirements, or write a direct prompt
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* View Mode Toggle */}
          <div className="flex border border-border rounded-md">
            <button
              onClick={() => setViewMode('chat')}
              className={`px-3 py-1 text-xs flex items-center gap-1 rounded-l-md transition-colors ${
                viewMode === 'chat'
                  ? 'bg-orange-500 text-white'
                  : 'bg-background text-muted-foreground hover:text-foreground'
              }`}
            >
              <MessageSquare className="w-3 h-3" />
              Chat
            </button>
            <button
              onClick={() => setViewMode('text')}
              className={`px-3 py-1 text-xs flex items-center gap-1 rounded-r-md transition-colors ${
                viewMode === 'text'
                  ? 'bg-orange-500 text-white'
                  : 'bg-background text-muted-foreground hover:text-foreground'
              }`}
            >
              <FileText className="w-3 h-3" />
              Text
            </button>
          </div>

          {/* Text Mode Edit/Save Buttons */}
          {viewMode === 'text' && (
            <>
              {isEditing ? (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCancel}
                    disabled={isSaving}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </Button>
                </>
              ) : (
                <Button
                  size="sm"
                  onClick={() => setIsEditing(true)}
                >
                  Edit
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {viewMode === 'chat' ? (
          <ChatInterface
            projectId={activeProjectId!}
            showPlanShortcut
            onConversationComplete={(messages) => {
              // When conversation is completed, save the conversation summary
              const summary = messages.map(m => `${m.role}: ${m.content}`).join('\n\n');
              updateProject(activeProjectId!, { agentPrompt: summary });
            }}
          />
        ) : (
          <div className="h-full overflow-auto p-4">
            {isEditing ? (
              <textarea
                value={agentPrompt}
                onChange={(e) => setAgentPrompt(e.target.value)}
                className="w-full h-full min-h-[200px] px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none font-mono text-sm"
                placeholder="Describe what you want the agent to create (e.g., skills, subskills, integrations)..."
                autoFocus
              />
            ) : (
              <div className="prose prose-sm dark:prose-invert max-w-none">
                {agentPrompt ? (
                  <div className="whitespace-pre-wrap font-mono text-sm text-foreground bg-muted p-4 rounded-md border border-border">
                    {agentPrompt}
                  </div>
                ) : (
                  <div className="text-center text-muted-foreground py-12">
                    <p className="text-sm">No agent prompt set</p>
                    <p className="text-xs mt-2">
                      Use Chat mode to have a conversation with AI, or click "Edit" to write a direct prompt
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
